/** @jsxImportSource ./simboluri-jsx */
import { bani, baniPeM2 } from "./bani";
import { CatalogVerificari, StepState, etapaTitlu, fmtDate } from "./tipuri";
import type { CostCompact, PasFaraVerdicte, RandAcoperire, RandModificare,
  RezultatPasul19, StarePicate, VerificarePicata } from "./tipuri";
import TitluVerificare, { useCatalogVerificari } from "./Verificare";
import { useStructure } from "./StructureContext";
import { numeElement, numeFaza, elementulRandului } from "../nav/selectie";
import Marime from "./Marime";
import HoverCard from "./HoverCard";
import { linkAncora } from "./legaturi";
import TabelAvertismentePas4 from "./OpuriRefuzate";
import { numar, t, tn, useT } from "../i18n";
import type { Cheie } from "../i18n";
import { ro } from "../i18n/ro";
import { traduceRezultat } from "./texteRezultat";

// Pasul 19 — soluțiile solverului unic (2 = calibrarea, 3–22 = repararea,
// 23–25 = confirmarea celor mai bune trei la rezoluția plină, 26–30 =
// repararea la rezoluția plină), fiecare cu raportul ei.
//
// ORDINEA SECȚIUNILOR S-A INVERSAT: întrebarea cu care
// deschizi pagina e «ce mai pică ACUM», nu «de unde am plecat». Deci:
//   1. verificările picate pe soluția CURENTĂ (tabel pe categorii + lista
//      detaliată + metrica de grinzi aparente + devizul pe rețete);
//   2. de unde a plecat și ce s-a schimbat: soluția precedentă, câte picau
//      pe ea, tabelul modificărilor (verificarea picată → algoritmul și
//      abordarea → op-urile aplicate) și abia la urmă detaliile ei.
// Istoricul rămâne accesibil, dar nu mai stă în fața răspunsului.
// Fără Gantt, fără grafice, fără jurnal — raportul de mai sus e pagina.

// MĂRIMEA soluției, nu un verdict: câți metri de grindă rămân la vedere în
// camere. Stă lângă tabelul de picate fiindcă se citește împreună cu el —
// o soluție cu mai puține picate, dar cu mai mulți metri de grindă aparentă,
// e un alt fel de compromis, nu un progres curat.
function MetricaGrindaAfara({
  m,
}: {
  m?: {
    grinda_in_afara_peretilor?: {
      metri: number;
      niveluri_exceptate?: string[];
      pe_nivel?: { nivel: number; nume: string; metri: number;
        exceptat: boolean }[];
    };
  } | null;
}) {
  const g = m?.grinda_in_afara_peretilor;
  if (!g) return null;
  const peNivel = (g.pe_nivel ?? []).filter((n) => !n.exceptat && n.metri > 0);
  return (
    <div className="sg-muted">
      {t("struct.opt.grinda_in_afara", { m: g.metri })}{" "}
      <Marime cheie="grinda_in_afara_peretilor" />
      {peNivel.length > 0 && t("struct.opt.grinda_pe_nivel", {
        lista: peNivel
          .map((n) => t("struct.opt.grinda_nivel_metri", { nume: n.nume, m: n.metri }))
          .join(", "),
      })}
      {(g.niveluri_exceptate?.length ?? 0) > 0 && t("struct.opt.grinda_exceptate", {
        lista: g.niveluri_exceptate!.join(", "),
      })}
    </div>
  );
}

// tabloul de ACOPERIRE: din ce pași s-au adunat verificările. E dovada că
// „0 picate" înseamnă chiar zero pe tot proiectul, nu zero din ce s-a citit.
function TabelAcoperire({
  acoperire,
  faraVerdicte,
}: {
  acoperire?: RandAcoperire[] | null;
  faraVerdicte?: PasFaraVerdicte[] | null;
}) {
  useT();
  if (!acoperire?.length) return null;
  const lipsa = acoperire.filter((a) => !a.citit);
  const total = acoperire.reduce((a, r) => a + (r.nr_verificari ?? 0), 0);
  const nrPasi = acoperire.length + (faraVerdicte?.length ?? 0);
  return (
    <details style={{ marginTop: 6 }} className="cv-acoperire">
      <summary className="sg-muted">
        {t("struct.opt.ce_s_a_centralizat", {
          total: numar(total),
          citite: acoperire.length - lipsa.length,
          toti: acoperire.length,
          fara: faraVerdicte?.length
            ? t("struct.opt.din_pasi_lantului", { n: nrPasi })
            : "",
          lipsa: lipsa.length > 0
            ? t("struct.opt.atentie_pasi_lipsa", { n: lipsa.length })
            : "",
        })}
      </summary>
      <table className="sg-table" style={{ marginTop: 6 }}>
        <thead>
          <tr>
            <th>{t("struct.opt.th_pasul")}</th>
            <th>{t("struct.opt.verificari_citite")}</th>
            <th>{t("struct.opt.th_picate")}</th>
            <th>{t("struct.opt.limita")}</th>
          </tr>
        </thead>
        <tbody>
          {acoperire.map((a) => (
            <tr key={a.pas}>
              <td className={a.citit ? undefined : "cv-lipsa"}>
                {a.nume}
                {!a.citit && t("struct.opt.rezultat_lipsa")}
              </td>
              <td className="sg-mono">{a.citit ? a.nr_verificari : "—"}</td>
              <td className="sg-mono">{a.nr_picate ?? 0}</td>
              <td className="sg-mono">{a.nr_la_limita ?? 0}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {!!faraVerdicte?.length && (
        <div className="sg-muted" style={{ marginTop: 8 }}>
          <strong>{t("struct.opt.pasii_lantului_emit")}</strong>{" "}
          {t("struct.opt.enumerati_explicit")}
          <ul style={{ margin: "4px 0 0", paddingLeft: 18 }}>
            {faraVerdicte.map((p) => (
              <li key={p.pas}>
                <span className="sg-mono">{p.pas}</span> — {p.de_ce}
              </li>
            ))}
          </ul>
        </div>
      )}
    </details>
  );
}

// tabelul „câte verificări din fiecare tip pică" — sortat descrescător.
// Titlul fiecărei categorii e explicat la hover — aceeași componentă și
// același stil ca pe paginile pașilor 6, 10, 13, 14 și 15 (v. Verificare.tsx).
// LISTA verificărilor picate dintr-o categorie, desfășurată sub rândul ei.
// Fiecare rând spune la ce se referă verificarea (element, nivel, cifrele cu
// care a picat) și duce, printr-un link adresabil, în pasul care a emis-o, cu
// elementul SELECTAT. Ancora vine de la server — v. `optimizare.ancora_verificare`.
function ListaPicate({
  picate,
  projectId,
  solutie,
  linkuri,
}: {
  picate: VerificarePicata[];
  projectId: string;
  solutie?: string | null;
  // linkurile duc în CLONA soluției despre care e tabelul. Secțiunea „soluția
  // precedentă" o cunoaște doar când raportul îi știe numele (`plecare.solutie`);
  // fără el am trimite omul în altă geometrie decât cea care a picat, așa că
  // rândurile rămân fără link — detaliile se văd oricum.
  linkuri: boolean;
}) {
  useT();
  if (!picate.length)
    return (
      <div className="sg-muted" style={{ fontSize: 12 }}>
        {t("struct.opt.lista_nu_e_in_raport")}
      </div>
    );
  return (
    <ul className="cv-picate">
      {picate.map((p, i) => {
        const l = linkuri ? linkAncora(projectId, solutie, p.ancora) : null;
        return (
          <li key={i}>
            <div className="cv-rand">
              <span className="sg-mono">{p.element || "—"}</span>
              <span>{p.verificare}</span>
              {p.RE != null && (
                <span className="sg-mono st-bad">R/E {p.RE}</span>
              )}
              {p.prioritate === "ridicata" && (
                <span className="sg-muted" style={{ fontSize: 11 }}>
                  {t("struct.opt.prioritate_ridicata")}
                </span>
              )}
              {l && (
                <a href={l.href} title={l.titlu} style={{ fontSize: 12 }}>
                  → {l.eticheta}
                </a>
              )}
            </div>
            {p.descriere && (
              <div className="sg-muted" style={{ fontSize: 12 }}>
                {p.descriere}
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );
}

function TabelPicate({
  nrPicate,
  peTipuri,
  picate,
  acoperire,
  faraVerdicte,
  catalog,
  projectId,
  solutie,
  linkuri,
}: {
  nrPicate?: number | null;
  peTipuri?: Record<string, number> | null;
  picate?: VerificarePicata[] | null;
  acoperire?: RandAcoperire[] | null;
  faraVerdicte?: PasFaraVerdicte[] | null;
  catalog: CatalogVerificari | null;
  projectId: string;
  solutie?: string | null;
  linkuri: boolean;
}) {
  useT();
  const intrari = Object.entries(peTipuri ?? {}).sort((a, b) => b[1] - a[1]);
  if (nrPicate == null && !intrari.length)
    return <div className="sg-muted">{t("struct.opt.fara_date_despre")}</div>;
  if (!intrari.length)
    return (
      <>
        <div className="fem-ok">{t("struct.opt.0_verificari_picate")}</div>
        <TabelAcoperire acoperire={acoperire} faraVerdicte={faraVerdicte} />
      </>
    );
  // lista salvată e plafonată pe server (`STARE_MAX_PICATE`): dacă o categorie
  // are mai puține rânduri decât numărul ei, spunem asta — o listă tăiată în
  // tăcere se citește drept «astea sunt toate»
  const peCategorie = new Map<string, VerificarePicata[]>();
  for (const p of picate ?? []) {
    const t = String(p.tip ?? "");
    if (!peCategorie.has(t)) peCategorie.set(t, []);
    peCategorie.get(t)!.push(p);
  }
  return (
    <>
      <table className="sg-table" style={{ marginTop: 6 }}>
        <thead>
          <tr>
            <th>{t("struct.opt.th_categoria_hover")}</th>
            <th>{t("struct.opt.cate_pica")}</th>
          </tr>
        </thead>
        <tbody>
          {intrari.map(([tip, nr]) => {
            const ale = peCategorie.get(tip) ?? [];
            return (
              <tr key={tip}>
                <td>
                  <TitluVerificare tip={tip} catalog={catalog} />
                  <details style={{ marginTop: 4 }}>
                    <summary className="sg-muted" style={{ cursor: "pointer", fontSize: 12 }}>
                      {ale.length && ale.length < nr
                        ? t("struct.opt.vezi_din_cele", { n: ale.length, total: nr })
                        : nr === 1
                          ? t("struct.opt.vezi_verificarea_picata")
                          : t("struct.opt.vezi_toate_cele", { n: nr })}
                    </summary>
                    <ListaPicate picate={ale} projectId={projectId}
                                 solutie={solutie} linkuri={linkuri} />
                  </details>
                </td>
                <td className="sg-mono">{nr}</td>
              </tr>
            );
          })}
          <tr>
            <td>
              <strong>Total</strong>
            </td>
            <td className="sg-mono">
              <strong>
                {nrPicate ?? intrari.reduce((a, [, n]) => a + n, 0)}
              </strong>
            </td>
          </tr>
        </tbody>
      </table>
      <TabelAcoperire acoperire={acoperire} faraVerdicte={faraVerdicte} />
    </>
  );
}

// devizul soluției: articolele comerciale, cu costul DIRECT în față.
// Ordonarea soluțiilor se face pe costul direct (material + manoperă);
// procentele de indirecte sunt multiplicatori care nu depind de mutare.
function Deviz({ cost }: { cost?: CostCompact }) {
  useT();
  if (!cost)
    return (
      <div className="sg-muted">
        {t("struct.opt.deviz_indisponibil")}
      </div>
    );
  if (cost.total_direct_eur == null && cost.total_lei != null)
    return (
      <div className="sg-muted">
        {t("struct.opt.deviz_vechi", {
          lei: numar(Math.round(cost.total_lei)),
          eur: bani(cost.total_lei / 4.98),
        })}
      </div>
    );
  const ind = cost.indicatori;
  return (
    <>
      <div style={{ margin: "6px 0" }}>
        <strong>{bani(cost.total_direct_eur)}</strong>
        <span className="sg-muted">{t("struct.opt.cost_direct")}</span>
        {cost.eur_pe_m2 != null && (
          <span className="sg-muted">
            {" "}· {baniPeM2(cost.eur_pe_m2)}
            {t("struct.opt.arie_desfasurata", {
              v: numar(cost.arie_desfasurata_m2),
            })}
          </span>
        )}
        {cost.total_eur != null && cost.total_eur !== cost.total_direct_eur && (
          <span className="sg-muted">
            {t("struct.opt.cu_indirecte", { v: bani(cost.total_eur) })}
          </span>
        )}
      </div>
      <div className="sg-muted" style={{ fontSize: 12, marginBottom: 6 }}>
        {t("struct.opt.materiale_manopera", {
          mat: bani(cost.total_material_eur),
          man: bani(cost.total_manopera_eur),
        })}
        {ind?.kg_pe_m3 != null && (
          <>
            {t("struct.opt.kg_otel_m3", { v: ind.kg_pe_m3 })}
            {ind.cofraj_m2_pe_m3 != null
              && t("struct.opt.cofraj_m2_m3", { v: ind.cofraj_m2_pe_m3 })}
            {ind.in_banda === false && (
              <span title={t("struct.opt.reperul_devizului_referi")}>
                {t("struct.opt.in_afara_benzii")}
              </span>
            )}
          </>
        )}
        {cost.precizie && cost.precizie !== "exact" && (
          <>{t("struct.opt.precizie", { p: cost.precizie })}</>
        )}
      </div>
      {(cost.articole ?? []).length > 0 && (
        <table className="sg-table" style={{ marginTop: 4 }}>
          <thead>
            <tr>
              <th>{t("struct.ret.th_articol")}</th>
              <th>{t("struct.ret.th_um")}</th>
              <th>{t("struct.ret.th_cantitate")}</th>
              <th>Total</th>
            </tr>
          </thead>
          <tbody>
            {(cost.articole ?? []).map((a, i) => (
              <tr key={i}>
                <td>
                  {a.articol}
                  {a.precizie && a.precizie !== "exact" && (
                    <span className="sg-muted"> [{a.precizie}]</span>
                  )}
                </td>
                <td className="sg-mono">{a.um ?? "—"}</td>
                <td className="sg-mono">
                  {numar(a.cantitate)}
                </td>
                <td className="sg-mono">{bani(a.total_eur)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  );
}

// Numele op-urilor de overlay, în românește. Fără dicționarul ăsta coloana
// „ce s-a schimbat efectiv" ar arăta `redimensioneaza_grinda` — cheia din cod,
// nu modificarea. Op-urile sunt cele din `model._aplica_modificari`.
// Numele op-ului și ale parametrilor lui sunt CHEI: textul se scrie în limba
// omului, iar codul rămâne cel din `model._aplica_modificari`.
const numeOp = (op: string): string =>
  (`struct.opt.op.${op}` in ro ? t(`struct.opt.op.${op}` as Cheie) : op);
const numeParam = (p: string): string =>
  (`struct.opt.par.${p}` in ro ? t(`struct.opt.par.${p}` as Cheie) : p);

const OP_UNITATI: Record<string, string> = {
  b_cm: "cm",
  h_cm: "cm",
  L_cm: "cm",
};

// Cheile, nu textele: eticheta se cere la randare, în limba de atunci.
const STARE_ETICHETA: Record<string, { text: Cheie; clasa: string }> = {
  aplicata: { text: "struct.opt.aplicata", clasa: "so-stare-ok" },
  epuizata: {
    text: "struct.fs.stare_epuizata",
    clasa: "so-stare-stop",
  },
  acoperita: {
    text: "struct.opt.acoperita_alta_modificar",
    clasa: "so-stare-neutra",
  },
  // faza 2 nu are voie să redimensioneze un element pe care faza 1
  // (conformarea) l-a dimensionat pentru rigiditatea globală
  refuzata: {
    text: "struct.opt.refuzata_element_ingheat",
    clasa: "so-stare-stop",
  },
};

function fmtValoareOp(cheie: string, v: unknown): string {
  if (Array.isArray(v)) return v.map((x) => String(x)).join(" + ");
  const u = OP_UNITATI[cheie];
  if (typeof v === "number")
    return u ? `${v} ${u}` : String(Math.round(v * 1000) / 1000);
  return String(v);
}

// un op de overlay, citibil: «redimensionează grinda GR2 — lățimea b 30 cm ·
// înălțimea h 70 cm». `id_plan` iese în față fiindcă el spune PE CE s-a
// lucrat; restul parametrilor rămân în ordinea din op.
function Op({ o }: { o: Record<string, unknown> }) {
  const tip = String(o.op ?? "");
  const id = o.id_plan != null ? String(o.id_plan) : null;
  const rest = Object.entries(o).filter(
    ([k, v]) => k !== "op" && k !== "id_plan" && v != null
  );
  return (
    <div className="so-op">
      <span className="so-op-nume">
        {numeOp(tip)}
      </span>
      {id && <span className="sg-mono">{id}</span>}
      {rest.length > 0 && (
        <span className="so-op-param">
          {rest
            .map(([k, v]) => `${numeParam(k)} ${fmtValoareOp(k, v)}`)
            .join(" · ")}
        </span>
      )}
    </div>
  );
}

// TABELUL MODIFICĂRILOR — („lista de modificări
// e scrisă foarte neinteligibil"). Ce era greșit și ce s-a schimbat:
//
//   * cheia tehnică („grinda_P100-1") apărea ca nume de verificare, deși
//     catalogul are titlul omenesc și explicația la hover — acum coloana
//     folosește `TitluVerificare`, ca pe toate celelalte pagini;
//   * algoritmul, pseudocodul și abordările sărite stăteau înghesuite în
//     ACEEAȘI celulă — acum abordarea aleasă se citește dintr-o privire
//     („a 2-a din 3: înalță grinda"), iar raționamentul stă într-un
//     `details` care nu mai împinge tabelul;
//   * „modificarea aplicată" era o propoziție de proză, iar op-urile CHIAR
//     aplicate (cu cifrele lor) nu se vedeau deloc — acum se vede și fraza,
//     și op-urile, și reglajele;
//   * starea rândului era doar o culoare de fundal, deci ilizibilă pentru
//     cine nu știe convenția — acum are etichetă scrisă.
function TabelModificari({
  randuri,
  catalog,
  notaPlan,
}: {
  randuri: RandModificare[];
  catalog: CatalogVerificari | null;
  // nota planului intră AICI, nu deasupra tabelului: când n-a fost nicio
  // modificare, ea E explicația, iar afișată separat ieșeau două propoziții
  // care spuneau același lucru cu alte cuvinte.
  notaPlan?: string | null;
}) {
  useT();
  if (!randuri.length)
    return (
      <div className="sg-muted">
        {notaPlan ?? t("struct.opt.nicio_modificare")}
      </div>
    );
  const nrAplicate = randuri.filter((r) => r.stare === "aplicata").length;
  const nrEpuizate = randuri.filter((r) => r.stare === "epuizata").length;
  const nrRefuzate = randuri.filter((r) => r.stare === "refuzata").length;
  const nrRest = randuri.length - nrAplicate - nrEpuizate - nrRefuzate;
  return (
    <>
      <div className="sg-muted" style={{ marginTop: 6 }}>
        {t("struct.opt.probleme_tratate", {
          probleme: tn("struct.opt.probleme#n", randuri.length),
          aplicate: nrAplicate,
          epuizate: nrEpuizate > 0
            ? t("struct.opt.cu_catalogul_epuizat", { n: nrEpuizate }) : "",
          refuzate: nrRefuzate > 0
            ? t("struct.opt.pe_elemente_inghetate", {
                n: tn("struct.opt.refuzate#n", nrRefuzate) })
            : "",
          rest: nrRest > 0
            ? t("struct.opt.de_alte_modificari", {
                n: tn("struct.opt.acoperite#n", nrRest) })
            : "",
        })}
      </div>
      <table className="sg-table so-mod" style={{ marginTop: 6 }}>
        <thead>
          <tr>
            <th className="so-nr">#</th>
            <th>{t("struct.opt.th_elementul")}</th>
            <th>{t("struct.opt.verificarea_a_cerut")}</th>
            <th>{t("struct.opt.lipsea")}</th>
            <th>{t("struct.opt.abordarea_aleasa")}</th>
            <th>{t("struct.opt.ce_s_a_schimbat")}</th>
          </tr>
        </thead>
        <tbody>
          {randuri.map((r, i) => {
            const tip = String(r.cheie ?? "").split("|")[0];
            const st = STARE_ETICHETA[String(r.stare ?? "")];
            const pseudocod = r.pseudocod ?? [];
            const sarite = r.sarite ?? [];
            const ops = r.ops ?? [];
            const reglaje = Object.entries(r.reglaje ?? {});
            return (
              <tr key={i} className={r.stare ? `so-r-${r.stare}` : undefined}>
                <td className="sg-mono so-nr">{i + 1}</td>
                <td>
                  <span className="sg-mono so-elem">{r.element || "—"}</span>
                  {r.nr_puncte != null && r.nr_puncte > 1 && (
                    <div className="sg-muted so-mic">
                      {t("struct.opt.pica_in_puncte", { n: r.nr_puncte })}
                    </div>
                  )}
                </td>
                <td>
                  <TitluVerificare
                    tip={tip}
                    nume={r.verificare}
                    catalog={catalog}
                    aratPasul={false}
                  />
                </td>
                <td>
                  <span className="sg-mono so-sever">
                    {r.severitate_pct != null ? `${r.severitate_pct} %` : "—"}
                  </span>
                  {r.RE != null && (
                    <div className="sg-muted so-mic">R/E {r.RE}</div>
                  )}
                </td>
                <td>
                  <div className="so-abordare">
                    {r.abordare != null
                      ? t("struct.opt.abordarea_din", { n: r.abordare, total: r.abordari_total })
                      : ""}
                    <strong>{r.algoritm}</strong>
                  </div>
                  {(pseudocod.length > 0 || sarite.length > 0) && (
                    // raționamentul în CARTONAȘUL casei, nu într-un `details`
                    // din celulă: pseudocodul are linii de ~40 de caractere,
                    // iar coloana are 190 px — desfășurat în celulă se rupea
                    // cuvânt cu cuvânt. Cartonașul e derulabil și se fixează
                    // la clic, deci formula se poate și copia.
                    <HoverCard
                      latime={420}
                      eticheta={t("struct.opt.a_ales_algoritmul")}
                      continut={
                        <>
                          {sarite.length > 0 && (
                            <>
                              <span className="fx-tip-title">
                                {t("struct.opt.abordari_sarite_inainte")}
                              </span>
                              <ul className="so-sarite">
                                {sarite.map((x, k) => (
                                  <li key={k}>{x}</li>
                                ))}
                              </ul>
                            </>
                          )}
                          {pseudocod.length > 0 && (
                            <>
                              <span className="fx-tip-title">
                                {t("struct.opt.algoritmul_abordarii_ale")}
                              </span>
                              <pre className="sg-mono so-pseudocod">
                                {pseudocod.join("\n")}
                              </pre>
                            </>
                          )}
                        </>
                      }
                    >
                      <span className="so-cum">{t("struct.opt.a_ales")}</span>
                    </HoverCard>
                  )}
                </td>
                <td>
                  <div>{r.modificare}</div>
                  {ops.map((o, k) => (
                    <Op key={k} o={o} />
                  ))}
                  {reglaje.length > 0 && (
                    <div className="so-op">
                      <span className="so-op-nume">{t("struct.opt.reglaj")}</span>
                      <span className="so-op-param">
                        {reglaje.map(([k, v]) => `${k} = ${v}`).join(" · ")}
                      </span>
                    </div>
                  )}
                  {st && (
                    <div className={`so-stare ${st.clasa}`}>{t(st.text)}</div>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </>
  );
}

// Comparația dintre soluția precedentă și cea curentă, în CIFRE: câte
// verificări picau acolo, câte pică aici, și cât costă fiecare. Stă în capul
// secțiunii 2 fiindcă „de unde a plecat" fără delta nu spune nimic.
function Comparatie({
  plecare,
  final,
  solutie,
}: {
  plecare?: (StarePicate & { solutie?: string | null }) | null;
  final?: StarePicate | null;
  solutie?: string | null;
}) {
  useT();
  const a = plecare?.nr_picate;
  const b = final?.nr_picate;
  const delta = a != null && b != null ? b - a : null;
  return (
    <table className="sg-table so-cmp" style={{ marginTop: 6 }}>
      <thead>
        <tr>
          <th>{t("struct.opt.solutia")}</th>
          <th>{t("struct.opt.verificari_picate")}</th>
          <th>{t("struct.opt.cost_structura")}</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>
            {t("struct.opt.precedenta")}{" "}
            <span className="sg-mono so-elem">
              {plecare?.solutie ?? t("struct.opt.starea_de_plecare")}
            </span>
          </td>
          <td className="sg-mono">{a ?? "—"}</td>
          <td className="sg-mono">{bani(plecare?.cost?.total_direct_eur)}</td>
        </tr>
        <tr className="sg-tr-activ">
          <td>
            {t("struct.opt.curenta")}{" "}
            <span className="sg-mono so-elem">{solutie ?? "—"}</span>
          </td>
          <td className="sg-mono">
            {b ?? "—"}
            {delta != null && delta !== 0 && (
              <span className={delta < 0 ? "so-delta-bun" : "so-delta-rau"}>
                {" "}
                {delta > 0 ? `+${delta}` : delta}
              </span>
            )}
            {delta === 0 && <span className="sg-muted">{t("struct.opt.neschimbat")}</span>}
          </td>
          <td className="sg-mono">{bani(final?.cost?.total_direct_eur)}</td>
        </tr>
      </tbody>
    </table>
  );
}

export default function StepOptimizare({
  projectId,
  state,
  running,
}: {
  projectId: string;
  state: StepState | null;
  running: boolean;
}) {
  useT();
  const prg = state?.progres;
  const ruleaza = !!(running || prg);

  // Raportul e al rândului PRIVIT — Pasul 19 din spațiul lui, citit de
  // furnizorul structurii ca orice pas. Selectul cu toate soluțiile a plecat
  // (A10): soluția se schimbă din lanț, nu din pagina unui pas.
  const { solutiaCurenta: solutia, solutii, solutie } = useStructure();
  const rez = (state?.result as unknown as RezultatPasul19 | null) ?? null;
  const element = solutia ? elementulRandului(solutii, solutia.id) : null;
  // explicațiile categoriilor (o singură cerere pe sesiune)
  const catalog = useCatalogVerificari();

  // panoul de rulare — compact: unde e acum orchestratorul/workerul
  const panouRulare = ruleaza ? (
    <div className="st-status" style={{ marginBottom: 12 }}>
      <div className="st-spinner" />
      <div>
        <strong>{t("struct.opt.pasul_19_ruleaza")}</strong>
        <div className="sg-muted">
          {prg ? etapaTitlu(prg) : t("struct.opt.porneste")}
          {prg?.detaliu?.acum
            ? t("struct.opt.detaliu_acum", { acum: prg.detaliu.acum }) : ""}
        </div>
      </div>
    </div>
  ) : null;

  const err = (state?.result as { error?: string } | null)?.error;
  const raport = rez?.raport_solutie ?? null;

  return (
    <div className="st-result">
      {panouRulare}
      {err && <div className="sg-error">{t("struct.opt.eroare", { motiv: err })}</div>}

      {!solutia ? (
        <div className="sg-muted st-empty">
          {t("struct.opt.pasul_a_rulat")}
        </div>
      ) : (
        <>
          <div className="sg-muted" style={{ marginTop: 6 }}>
            <b>{element ? numeElement(element) : solutia.label}</b>
            {solutia.faza && solutia.faza !== "schita" ? ` · ${numeFaza(solutia.faza)}` : ""}
            {solutia.structura?.cifre.scor != null
              ? t("struct.opt.scor_val", { v: solutia.structura.cifre.scor }) : ""}
            {solutia.motiv ? t("struct.opt.motiv_val", { v: traduceRezultat(solutia.motiv) }) : ""}
          </div>

          {!raport && (
            <div className="sg-muted" style={{ marginTop: 10 }}>
              {solutia.rulare
                ? t("struct.opt.raport_apare_cand")
                : t("struct.opt.solutia_fara_raport")}
            </div>
          )}

          {/* DE CE s-a oprit căutarea — singura frază a serverului despre asta
              (`stuck_reason`, în detaliu, când nu mai propune nimic) */}
          {rez?.stuck_reason && (
            <div className="st-callout" style={{ marginTop: 10 }}>
              <b>{t("struct.opt.stuck_titlu")}</b>: {rez.stuck_reason}
            </div>
          )}
          {(rez?.modificari_facute?.length ?? 0) > 0 && (
            <details style={{ marginTop: 8 }}>
              <summary className="sg-muted">
                {t("struct.opt.modificari_facute_titlu", { n: rez!.modificari_facute!.length })}
              </summary>
              <ul>
                {rez!.modificari_facute!.map((m, i) => <li key={i}>{m}</li>)}
              </ul>
            </details>
          )}
          {(rez?.recomandari_geometrie?.length ?? 0) > 0 && (
            <details style={{ marginTop: 8 }} open>
              <summary className="sg-muted">
                {t("struct.opt.recomandari_geo_titlu", { n: rez!.recomandari_geometrie!.length })}
              </summary>
              <ul>
                {rez!.recomandari_geometrie!.map((r, i) => (
                  <li key={i}>
                    <b>{r.titlu ?? r.ce ?? r.cheie ?? ""}</b>
                    {r.titlu && r.ce ? ` — ${r.ce}` : ""}
                    {r.de_ce ? <span className="sg-muted"> · {r.de_ce}</span> : null}
                    {r.impact != null ? (
                      <span className="sg-muted"> · {t("struct.opt.recomandare_impact", { n: r.impact })}</span>
                    ) : null}
                  </li>
                ))}
              </ul>
            </details>
          )}

          {raport && (
            <>
              {/* 1. soluția CURENTĂ — răspunsul la întrebarea cu care se
                   deschide pagina: ce mai pică acum */}
              <h3>
                {t("struct.opt.h3_picate_curenta", {
                  verdict: rez?.verdict
                    ? t("struct.opt.verdict_dash", { v: rez.verdict }) : "",
                  scor: rez?.scor != null
                    ? t("struct.opt.scor_val", { v: rez.scor }) : "",
                })}
              </h3>
              <div className="sg-muted">
                {raport.durata_min != null
                  ? t("struct.opt.durata_min", { n: raport.durata_min })
                  : ""}
                {raport.rezolutie?.h_mesh_m != null
                  && t("struct.opt.rezolutia_lantului", {
                    sep: raport.durata_min != null ? " · " : "",
                    h: raport.rezolutie.h_mesh_m,
                    fel: t(raport.fidelitate === "final"
                      ? "struct.opt.rezolutia_plina" : "struct.opt.rezolutia_proba"),
                  })}
              </div>
              {/* CE A HOTĂRÂT analiza: trecerea de fază, bias-ul de
                   rezoluție, epuizarea pârghiilor. Erau deciziile arborelui,
                   într-un jurnal global; acum fiecare soluție le poartă pe
                   ale ei. */}
              {(raport.note ?? []).length > 0 && (
                <ul className="sg-muted" style={{ margin: "4px 0" }}>
                  {(raport.note ?? []).map((x, i) => (
                    <li key={i}>{x}</li>
                  ))}
                </ul>
              )}
              <TabelPicate
                nrPicate={raport.final?.nr_picate}
                peTipuri={raport.final?.picate_pe_tipuri}
                picate={raport.final?.picate}
                acoperire={raport.final?.acoperire}
                faraVerdicte={raport.final?.pasi_fara_verdicte}
                catalog={catalog}
                projectId={projectId}
                solutie={solutie ?? undefined}
                linkuri
              />
              {raport.final?.nota && (
                <div className="sg-muted">{raport.final.nota}</div>
              )}
              <MetricaGrindaAfara m={raport.final?.metrici} />
              <h4 style={{ marginBottom: 2 }}>
                {t("struct.opt.costul_curente")}
              </h4>
              <Deviz cost={raport.final?.cost} />

              {/* 2. de unde a plecat și ce s-a schimbat — istoricul, ca
                   context pentru cifrele de mai sus */}
              <h3>{t("struct.opt.2_unde_a")}</h3>
              <Comparatie
                plecare={raport.plecare}
                final={raport.final}
                solutie={solutie ?? undefined}
              />
              {raport.fidelitate === "final" &&
                raport.rezolutie?.h_mesh_m != null &&
                raport.rezolutie?.h_mesh_baza_m != null &&
                raport.plecare?.nr_picate != null &&
                raport.final?.nr_picate != null &&
                raport.final.nr_picate > raport.plecare.nr_picate && (
                  <div className="st-warn">
                    {t("struct.opt.atentie_comparatie", {
                      h: raport.rezolutie.h_mesh_m,
                    })}
                  </div>
                )}
              {/* CE PROPUNE mai departe. Orchestratorul alege dintre ele pe
                   scor; aici se vede ce a pus generatorul pe masă. */}
              {(raport.recomandari ?? []).length > 0 && (
                <>
                  <h4 style={{ marginBottom: 2 }}>
                    {t("struct.opt.ce_se_recomanda", {
                      n: (raport.recomandari ?? []).length,
                    })}
                  </h4>
                  <ul className="sg-muted" style={{ margin: "4px 0" }}>
                    {(raport.recomandari ?? []).map((v, i) => (
                      <li key={i}>
                        <b>{v.label ?? v.key}</b>
                        {v.reason
                          ? t("struct.opt.reason_dash", { v: v.reason }) : ""}
                      </li>
                    ))}
                  </ul>
                </>
              )}

              <h4 style={{ marginBottom: 2 }}>{t("struct.opt.ce_s_a_modificat")}</h4>
              {raport.nota_plan && (raport.modificari ?? []).length > 0 && (
                <div className="sg-muted">{raport.nota_plan}</div>
              )}
              <TabelModificari
                randuri={raport.modificari ?? []}
                catalog={catalog}
                notaPlan={raport.nota_plan}
              />
              {(raport.amanate ?? []).length > 0 && (
                <details style={{ marginTop: 6 }}>
                  <summary className="sg-muted">
                    {t("struct.opt.amanate_titlu", {
                      n: (raport.amanate ?? []).length,
                    })}
                  </summary>
                  <ul className="sg-muted" style={{ margin: "4px 0" }}>
                    {(raport.amanate ?? []).map((a, i) => (
                      <li key={i}>
                        <span className="sg-mono">{a.element}</span> —{" "}
                        {t("struct.opt.amanat_rand", {
                          verificare: a.verificare, sev: a.severitate_pct,
                          motiv: a.motiv
                            ? t("struct.opt.amanat_motiv", { v: traduceRezultat(a.motiv) }) : "",
                        })}
                      </li>
                    ))}
                  </ul>
                </details>
              )}
              {(raport.netratate ?? []).length > 0 && (
                <details style={{ marginTop: 6 }}>
                  <summary className="sg-muted">
                    {t("struct.opt.netratate_titlu", {
                      n: (raport.netratate ?? []).length,
                    })}
                  </summary>
                  <ul className="sg-muted" style={{ margin: "4px 0" }}>
                    {(raport.netratate ?? []).map((n, i) => (
                      <li key={i}>
                        <span className="sg-mono">{n.element}</span> —{" "}
                        {t("struct.opt.netratat_rand", {
                          verificare: n.verificare ?? n.tip, motiv: n.motiv,
                        })}
                      </li>
                    ))}
                  </ul>
                </details>
              )}
              {(raport.registru_raport ?? []).length > 0 && (
                <details style={{ marginTop: 6 }}>
                  <summary className="sg-muted">
                    {t("struct.opt.registru_titlu", {
                      n: (raport.registru_raport ?? []).length,
                    })}
                  </summary>
                  <table className="sg-table" style={{ marginTop: 4 }}>
                    <thead>
                      <tr>
                        <th>{t("struct.opt.th_problema")}</th>
                        <th>{t("struct.opt.parghia")}</th>
                        <th>{t("struct.opt.th_promis")}</th>
                        <th>{t("struct.opt.th_livrat")}</th>
                        <th>{t("struct.opt.th_verdict")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(raport.registru_raport ?? []).map((r, i) => (
                        <tr key={i}>
                          <td className="sg-mono">{r.cheie}</td>
                          <td>{r.parghie}</td>
                          <td>−{r.promis} %</td>
                          <td>
                            {(r.livrat ?? 0) >= 0 ? "−" : "+"}
                            {Math.abs(r.livrat ?? 0)} %
                          </td>
                          <td>
                            {t(r.verdict === "rateu"
                              ? "struct.opt.rateu" : "struct.opt.livrat")}
                            {r.demotata ? t("struct.opt.demotata") : ""}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div className="sg-muted" style={{ fontSize: "0.9em" }}>
                    {t("struct.opt.mutare_promite_scadere")}
                  </div>
                </details>
              )}
              {(raport.avertismente_pas4 ?? []).length > 0 && (
                <TabelAvertismentePas4
                  brute={raport.avertismente_pas4 ?? []}
                  total={raport.avertismente_pas4_total}
                />
              )}

              <h4 style={{ marginBottom: 2 }}>
                {t("struct.opt.picate_precedenta", {
                  n: raport.plecare?.nr_picate != null
                    ? t("struct.opt.paren_n", { n: raport.plecare.nr_picate })
                    : "",
                })}
              </h4>
              <div className="sg-muted">
                {raport.plecare?.solutie
                  ? t("struct.opt.a_plecat_din", { v: raport.plecare.solutie })
                  : t("struct.opt.a_plecat_din_etapa")}
                {raport.plecare?.nota
                  ? t("struct.opt.nota_dash", { v: traduceRezultat(raport.plecare.nota) }) : ""}
              </div>
              <TabelPicate
                nrPicate={raport.plecare?.nr_picate}
                peTipuri={raport.plecare?.picate_pe_tipuri}
                picate={raport.plecare?.picate}
                acoperire={raport.plecare?.acoperire}
                faraVerdicte={raport.plecare?.pasi_fara_verdicte}
                catalog={catalog}
                projectId={projectId}
                solutie={raport.plecare?.solutie ?? undefined}
                linkuri={false}
              />
              <MetricaGrindaAfara m={raport.plecare?.metrici} />
              <h4 style={{ marginBottom: 2 }}>
                {t("struct.opt.costul_precedente")}
              </h4>
              <Deviz cost={raport.plecare?.cost} />
            </>
          )}
        </>
      )}

      {state?.updated_at && (
        <div className="sg-muted st-ran-at">
          Rulat: {fmtDate(state.updated_at)}
        </div>
      )}
    </div>
  );
}
